import { useUser } from "@clerk/react";
import { useEffect, useMemo, useState } from "react";

import { useI18n } from "~/i18n";
import { useMorrowClerkAuth } from "./morrowClerkAuth";
import { saveMorrowUserAvatarSeed } from "./morrowClerkProfileAvatarSave";
import { MorrowPersonAvatar } from "../workspace/MorrowPersonCard";
import { MorrowPersonAvatarPicker } from "../workspace/MorrowPersonAvatarPicker";
import {
  defaultMorrowDicebearAvatarSeed,
  normalizeMorrowDicebearAvatarSeed,
} from "../workspace/morrowPersonAvatarCatalog";
import {
  ensureUserAvatarSeed,
  useUserProfile,
  userProfileFromMetadata,
  writeUserProfile,
} from "../workspace/morrowUserProfileStore";

/** DiceBear portrait shown inside Clerk's Account -> Update profile form. */
export function MorrowClerkProfileAvatarSection({ expanded }: { readonly expanded: boolean }) {
  const { t } = useI18n();
  const { user } = useUser();
  const { userId } = useMorrowClerkAuth();
  const stored = useUserProfile();
  const [avatarSeed, setAvatarSeed] = useState(stored.avatarSeed);
  const [portraitImageUrl, setPortraitImageUrl] = useState(stored.portraitImageUrl);
  const [pending, setPending] = useState(false);
  const displayName = useMemo(
    () =>
      user?.fullName?.trim() ||
      user?.firstName?.trim() ||
      user?.primaryEmailAddress?.emailAddress ||
      t("team.you"),
    [t, user],
  );
  const resolvedAvatarSeed =
    normalizeMorrowDicebearAvatarSeed(avatarSeed) ?? defaultMorrowDicebearAvatarSeed(displayName);

  useEffect(() => {
    const profile = ensureUserAvatarSeed();
    setAvatarSeed(profile.avatarSeed);
  }, []);

  useEffect(() => {
    if (!user) return;
    const fromClerk = userProfileFromMetadata(user.unsafeMetadata);
    if (!fromClerk.avatarSeed) return;
    writeUserProfile({ avatarSeed: fromClerk.avatarSeed });
    setAvatarSeed(fromClerk.avatarSeed);
  }, [user]);

  const handlePortraitChange = (imageUrl: string) => {
    setPortraitImageUrl(imageUrl);
    writeUserProfile({ portraitImageUrl: imageUrl });
  };

  const handleChange = (seed: string) => {
    setAvatarSeed(seed);
    setPortraitImageUrl("");
    writeUserProfile({ portraitImageUrl: "" });
    if (!user) return;
    setPending(true);
    void saveMorrowUserAvatarSeed({
      user,
      userId,
      displayName,
      avatarSeed: seed,
    })
      .catch(() => {
        // Local selection stays visible even if remote sync fails.
      })
      .finally(() => {
        setPending(false);
      });
  };

  return (
    <div className="flex w-full min-w-0 flex-col gap-3">
      <div className="flex items-start gap-3">
        <MorrowPersonAvatar
          name={displayName}
          {...(portraitImageUrl ? { imageUrl: portraitImageUrl } : {})}
          avatarSeed={resolvedAvatarSeed}
          size="lg"
          className="size-16"
        />
        {!expanded ? (
          <p className="pt-1 text-xs leading-relaxed text-muted-foreground">
            {t("profile.clerk.avatarHint")}
          </p>
        ) : null}
      </div>
      {expanded ? (
        <MorrowPersonAvatarPicker
          value={resolvedAvatarSeed}
          portraitImageUrl={portraitImageUrl}
          disabled={pending}
          onChange={handleChange}
          onPortraitImageUrlChange={handlePortraitChange}
        />
      ) : null}
    </div>
  );
}
